import { ArrowUpRight, Globe2 } from 'lucide-react';
import Image from 'next/image';

interface WorkExamplesSectionProps {
  id?: string;
}

const examples = [
  {
    name: 'Duct Doctor DMV',
    url: 'https://ductdoctordmv.com',
    image: '/images/work/duct-doctor-dmv.png',
    description:
      'Real time data synchronization between HouseCall-Pro and Zoho CRM helped DuctDoctor DMV streamline their operations.',
    tags: ['API', 'Data synchronization', 'HouseCall-Pro', 'Zoho CRM', 'Service business', 'Launch support'],
  },
  {
    name: 'B&B Commercial Cleaning',
    url: 'https://bbcommercialcleaning.com',
    image: '/images/work/bb-commercial-cleaning.png',
    description: 'Customer website for a commercial cleaning business, built and hosted by DataConnector-Pro.',
    tags: ['Website', 'Small business', 'Deployment', 'Hosting'],
  },
  {
    name: "Snazzy Jazzy's Pet Salon",
    url: 'https://snazzyjazzyspetsalon.com',
    image: '/images/work/snazzy-jazzys-pet-salon.png',
    description: 'Customer website for a pet salon, built and managed by DataConnector-Pro on customer infrastructure.',
    tags: ['Website', 'Service business', 'Launch support', 'Wordpress', 'Customer infrastructure'],
  },
];

export default function WorkExamplesSection({ id }: WorkExamplesSectionProps) {
  return (
    <section id={id} className="w-full bg-[var(--color-paper)] py-16 md:py-24 lg:py-28">
      <div className="container px-4 md:px-6">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--color-copper)]">portfolio</p>
            <h2 className="mt-3 text-3xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
              Examples of satisfied customers
            </h2>
            <p className="mt-5 text-lg leading-8 text-[var(--color-muted)]">
              We help small and medium businesses modernize. Here are some examples of satisfied customers:
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {examples.map((example, index) => (
              <a
                key={example.url}
                href={example.url}
                target="_blank"
                rel="noreferrer"
                className={`group rounded-lg border border-[var(--color-line)] bg-white p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                  index === 0 ? 'md:col-span-2' : ''
                }`}
              >
                <div className="browser-frame mb-5 overflow-hidden rounded-md border border-[var(--color-line)] bg-[var(--color-canvas)]">
                  <div className="flex h-9 items-center gap-1.5 border-b border-[var(--color-line)] px-3">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#d86f55]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#d6a94d]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#6f9b6d]" />
                    <span className="ml-3 flex min-w-0 items-center gap-2 truncate text-xs font-medium text-[var(--color-muted)]">
                      <Globe2 className="h-3.5 w-3.5 shrink-0 text-[var(--color-signal)]" />
                      {new URL(example.url).hostname}
                    </span>
                  </div>
                  <div className="relative aspect-[16/9] overflow-hidden bg-[var(--color-canvas)]">
                    <Image
                      src={example.image}
                      alt={`${example.name} website preview`}
                      fill
                      sizes={index === 0 ? '(min-width: 1024px) 760px, 100vw' : '(min-width: 1024px) 370px, 100vw'}
                      className="object-cover object-top transition duration-500 group-hover:scale-[1.025]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition group-hover:opacity-100" />
                  </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-xl font-semibold text-[var(--color-ink)]">{example.name}</h3>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-[var(--color-copper)] transition group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <p className="mt-3 leading-7 text-[var(--color-muted)]">{example.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {example.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-[var(--color-line)] px-3 py-1 text-xs text-[var(--color-muted)]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </div>
        {/* <div className="mt-8 flex items-center gap-3 rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)] p-4 text-[var(--color-muted)]">
          <Server className="h-5 w-5 shrink-0 text-[var(--color-signal)]" />
          <p>
            Testimonials can be added later. For now, the site shows shipped examples without making claims we cannot
            display yet.
          </p>
        </div> */}
      </div>
    </section>
  );
}
